import React, { useState, useContext } from 'react';
import { sendOtp, verifyOtp, saveName } from '../services/authService';
import { AuthContext } from '../context/AuthContext';
import giftBox from '../assets/3d-render-blue-gift-box-with-ribbon-male-package 1.png';

/* ========================== ICONS ========================== */
const CheckCircleIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    style={{ flexShrink: 0 }}
  >
    <circle cx="12" cy="12" r="10" stroke="#34C759" strokeWidth="1.5" />
    <path
      d="M7.5 12L10.5 15L16.5 9"
      stroke="#34C759"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M18 6L6 18M6 6l12 12"
      stroke="#6b7280"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const SpinnerIcon = () => (
  <span
    style={{
      display: 'inline-block',
      width: '16px',
      height: '16px',
      border: '2px solid rgba(255,255,255,0.3)',
      borderTopColor: '#ffffff',
      borderRadius: '50%',
      animation: 'spin 0.7s linear infinite',
      verticalAlign: 'middle',
      marginRight: '8px',
      flexShrink: 0,
    }}
  />
);

/* ========================== CONSTANTS ========================== */
const MODAL_FEATURES = [
  'Get personalized credit card recommendations',
  'Resolve all your credit score queries',
  'Discover the best card for maximum returns',
];

/* ========================== LEFT PANEL ========================== */
const ModalLeftPanel = () => (
  <div style={s.leftPanel}>
    <p style={s.leftHeading}>Your first consultation call{'\n'}is absolutely</p>
    <p style={s.leftFree}>FREE</p>
    <div style={s.leftImageWrap}>
      <img src={giftBox} alt="Free consultation" style={s.leftImage} />
    </div>
    <div style={s.leftFeatures}>
      {MODAL_FEATURES.map((f) => (
        <div key={f} style={s.leftFeatureRow}>
          <CheckCircleIcon />
          <span style={s.leftFeatureText}>{f}</span>
        </div>
      ))}
    </div>
  </div>
);

/* ========================== MAIN COMPONENT ========================== */
export default function LoginModal({ onClose }) {
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [verifiedToken, setVerifiedToken] = useState(null);
  const [verifiedUser, setVerifiedUser] = useState(null);

  const timerRef = React.useRef(null);
  const { login } = useContext(AuthContext);

  /* ── Helpers ── */
  const formatMobile = () => {
    let cleaned = mobile.trim().replace(/\D/g, '');
    if (cleaned.length === 10) cleaned = `91${cleaned}`;
    if (cleaned.length !== 12 || !cleaned.startsWith('91')) return null;
    return cleaned;
  };

  const startTimer = (seconds = 60) => {
    setTimeLeft(seconds);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  };

  const formatTime = (secs) =>
    `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;

  React.useEffect(() => () => clearInterval(timerRef.current), []);

  const displayPhone =
    mobile.startsWith('91') && mobile.length === 12 ? mobile.slice(2) : mobile;
  const maskedPhone =
    displayPhone.length >= 10
      ? `${displayPhone.slice(0, 2)}XXXXXX${displayPhone.slice(-2)}`
      : displayPhone;

  /* ── Handlers ── */
  const handleSendOtp = async () => {
    setMessage('');
    setError('');
    const formattedMobile = formatMobile();
    if (!formattedMobile) {
      setError('Enter a valid 10 digit mobile number');
      return;
    }
    if (isSignup && (!firstName.trim() || !lastName.trim())) {
      setError('Please enter your first and last name');
      return;
    }
    try {
      setLoading(true);
      const res = await sendOtp(formattedMobile);
      if (res.response_code === 200) {
        const isNewNumber = res.is_new_user || res.new_user || !res.user_exists;
        if (isNewNumber && !isSignup) {
          setIsSignup(true);
          setMobile(formattedMobile);
          setError('');
          setMessage('New number detected. Please enter your name to sign up.');
          setLoading(false);
          return;
        }

        setMessage('OTP sent successfully');
        setMobile(formattedMobile);
        setStep(2);
        startTimer(60);
      } else {
        setError(
          res.errors?.[0] || res.response_message || 'Error sending OTP'
        );
      }
    } catch {
      setError('Server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    setMessage('');
    if (!otp.trim()) {
      setError('Enter OTP');
      return;
    }
    try {
      setLoading(true);
      const res = await verifyOtp(mobile, otp.trim());
      if (res.response_code === 200) {
        const token = res.user?.token;
        const user = res.user;
        if (!token) {
          setError('Authentication token missing');
          return;
        }
        const hasName = user.first_name && user.last_name;

        if (isSignup && hasName) {
          login(user, token);
          onClose?.();
          return;
        }

        if (!hasName) {
          if (firstName.trim() && lastName.trim()) {
            const nameRes = await saveName(
              token,
              firstName.trim(),
              lastName.trim(),
              `${mobile}@cardacademy.co`
            );
            if (nameRes.response_code !== 200) {
              setError(nameRes.errors?.[0] || 'Failed to save name');
              return;
            }
            login(
              {
                ...user,
                first_name: firstName.trim(),
                last_name: lastName.trim(),
              },
              token
            );
            onClose?.();
          } else {
            setVerifiedToken(token);
            setVerifiedUser(user);
            setStep(3);
          }
          return;
        }

        login(user, token);
        onClose?.();
      } else {
        setError(res.errors?.[0] || res.response_message || 'Incorrect OTP');
      }
    } catch {
      setError('Verification failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitName = async () => {
    setError('');
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your First and Last name to continue');
      return;
    }
    try {
      setLoading(true);
      const nameRes = await saveName(
        verifiedToken,
        firstName.trim(),
        lastName.trim(),
        `${mobile}@cardacademy.co`
      );
      if (nameRes.response_code !== 200) {
        setError(nameRes.errors?.[0] || 'Failed to save name');
        return;
      }
      login(
        {
          ...verifiedUser,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
        },
        verifiedToken
      );
      onClose?.();
    } catch {
      setError('Failed to save name. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    if (timeLeft > 0) return;
    setOtp('');
    setError('');
    handleSendOtp();
  };

  const handleChangePhone = () => {
    setStep(1);
    setOtp('');
    setError('');
    setMessage('');
    clearInterval(timerRef.current);
    setTimeLeft(0);
  };

  const switchToSignup = () => {
    setIsSignup(true);
    setStep(1);
    setError('');
    setMessage('');
    setMobile('');
    setOtp('');
    setFirstName('');
    setLastName('');
  };

  const switchToLogin = () => {
    setIsSignup(false);
    setStep(1);
    setError('');
    setMessage('');
    setMobile('');
    setOtp('');
    setFirstName('');
    setLastName('');
  };

  /* ── Render ── */
  return (
    <>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .login-modal-input:focus {
          outline: none;
          border-color: #1D4094;
          box-shadow: 0 0 0 3px rgba(29,64,148,0.12);
        }
        .login-modal-btn:hover { background-color: #1535a0 !important; }
        .login-modal-btn:active { transform: scale(0.98); }
        .login-modal-close:hover { background-color: #f3f4f6 !important; }

        /* Responsive: hide left panel on small screens */
        @media (max-width: 640px) {
          .login-modal-left { display: none !important; }
          .login-modal-right { width: 100% !important; min-width: unset !important; border-radius: 12px !important; }
          .login-modal-container { max-width: 95vw !important; }
        }
      `}</style>

      {/* Backdrop */}
      <div style={s.backdrop} onClick={onClose} />

      {/* Modal — scrollable wrapper so it never clips on small viewports */}
      <div style={s.scrollWrap}>
        <div style={s.modal} className="login-modal-container">
          {/* Left panel */}
          <div style={s.leftPanel} className="login-modal-left">
            <p style={s.leftHeading}>
              Your first consultation call{'\n'}is absolutely
            </p>
            <p style={s.leftFree}>FREE</p>
            <div style={s.leftImageWrap}>
              <img src={giftBox} alt="Free consultation" style={s.leftImage} />
            </div>
            <div style={s.leftFeatures}>
              {MODAL_FEATURES.map((f) => (
                <div key={f} style={s.leftFeatureRow}>
                  <CheckCircleIcon />
                  <span style={s.leftFeatureText}>{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right panel */}
          <div style={s.rightPanel} className="login-modal-right">
            {/* Close button */}
            <button
              style={s.closeBtn}
              className="login-modal-close"
              onClick={onClose}
            >
              <CloseIcon />
            </button>

            {/* Title */}
            <h1 style={s.title}>
              {step === 3 ? 'Sign Up' : isSignup ? 'Sign Up' : 'Login'}
            </h1>

            {/* ── STEP 1 ── */}
            {step === 1 && (
              <>
                {isSignup && (
                  <div style={s.fieldGroup}>
                    <label style={s.label}>Full Name</label>
                    <input
                      className="login-modal-input"
                      style={s.input}
                      type="text"
                      placeholder="Enter First Name"
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        setError('');
                      }}
                    />
                    <input
                      className="login-modal-input"
                      style={{ ...s.input, marginTop: '10px' }}
                      type="text"
                      placeholder="Enter Last Name"
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        setError('');
                      }}
                    />
                  </div>
                )}

                <div style={s.fieldGroup}>
                  <label style={s.label}>Contact Number</label>
                  <input
                    className="login-modal-input"
                    style={
                      error ? { ...s.input, borderColor: '#e24b4a' } : s.input
                    }
                    type="tel"
                    placeholder="Enter Mobile Number"
                    value={displayPhone}
                    onChange={(e) => {
                      let val = e.target.value.replace(/\D/g, '');
                      if (val.startsWith('91') && val.length === 12)
                        val = val.slice(2);
                      if (val.length > 10) val = val.slice(0, 10);
                      setMobile(val);
                      setError('');
                    }}
                  />
                </div>

                {error && <p style={s.errorText}>{error}</p>}
                {message && <p style={s.successText}>{message}</p>}

                <button
                  className="login-modal-btn"
                  style={s.btn}
                  onClick={handleSendOtp}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <SpinnerIcon />
                      Sending...
                    </>
                  ) : (
                    'REQUEST OTP'
                  )}
                </button>

                <p
                  style={s.switchText}
                  onClick={isSignup ? switchToLogin : switchToSignup}
                >
                  {isSignup
                    ? 'Existing User? Login'
                    : 'New to CardAcademy? Create an account'}
                </p>
              </>
            )}

            {/* ── STEP 2 ── */}
            {step === 2 && (
              <>
                <div style={s.fieldGroup}>
                  <div style={s.labelRow}>
                    <span style={{ ...s.label, marginBottom: 0 }}>
                      Please enter the OTP sent to {maskedPhone}
                    </span>
                    <button style={s.changeBtn} onClick={handleChangePhone}>
                      Change
                    </button>
                  </div>
                  <input
                    className="login-modal-input"
                    style={
                      error
                        ? {
                            ...s.input,
                            marginTop: '10px',
                            borderColor: '#e24b4a',
                          }
                        : { ...s.input, marginTop: '10px' }
                    }
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter OTP here"
                    value={otp}
                    onChange={(e) => {
                      setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                      setError('');
                    }}
                    maxLength={6}
                    autoComplete="one-time-code"
                  />
                </div>

                {error && <p style={s.errorText}>{error}</p>}
                {message && <p style={s.successText}>{message}</p>}

                <button
                  className="login-modal-btn"
                  style={s.btn}
                  onClick={handleVerifyOtp}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <SpinnerIcon />
                      Verifying...
                    </>
                  ) : (
                    'VERIFY OTP'
                  )}
                </button>

                <div style={s.resendRow}>
                  {timeLeft > 0 && (
                    <span style={s.timer}>{formatTime(timeLeft)}</span>
                  )}
                  <span style={s.resendText}>
                    Not received your code?{' '}
                    <span
                      style={timeLeft > 0 ? s.resendLinkDisabled : s.resendLink}
                      onClick={handleResend}
                    >
                      Resend code
                    </span>
                  </span>
                </div>
              </>
            )}

            {/* ── STEP 3 ── */}
            {step === 3 && (
              <>
                <div style={s.fieldGroup}>
                  <label style={s.label}>Full Name</label>
                  <input
                    className="login-modal-input"
                    style={
                      error ? { ...s.input, borderColor: '#e24b4a' } : s.input
                    }
                    type="text"
                    placeholder="Enter First Name"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      setError('');
                    }}
                  />
                  <input
                    className="login-modal-input"
                    style={
                      error
                        ? {
                            ...s.input,
                            marginTop: '10px',
                            borderColor: '#e24b4a',
                          }
                        : { ...s.input, marginTop: '10px' }
                    }
                    type="text"
                    placeholder="Enter Last Name"
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      setError('');
                    }}
                  />
                </div>

                {error && <p style={s.errorText}>{error}</p>}

                <button
                  className="login-modal-btn"
                  style={s.btn}
                  onClick={handleSubmitName}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <SpinnerIcon />
                      Submitting...
                    </>
                  ) : (
                    'SUBMIT'
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/* ========================== STYLES ========================== */
const s = {
  /* Backdrop */
  backdrop: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    zIndex: 1000,
  },

  /* Scroll wrap — sits on top of backdrop, centers modal, allows scroll on small screens */
  scrollWrap: {
    position: 'fixed',
    inset: 0,
    zIndex: 1001,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px 16px',
    boxSizing: 'border-box',
    overflowY: 'auto',
  },

  /* Modal container */
  modal: {
    display: 'flex',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 24px 60px rgba(0,0,0,0.22)',
    backgroundColor: '#ffffff',
    width: '100%',
    maxWidth: '880px',
    /* No fixed height — grows with content, never clips */
  },

  /* ── Left panel ── */
  leftPanel: {
    width: '440px',
    minWidth: '440px',
    backgroundColor: '#1D4094' /* ← correct brand blue */,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 32px',
    boxSizing: 'border-box',
  },
  leftHeading: {
    color: '#ffffff',
    fontSize: '20px',
    fontWeight: 600,
    textAlign: 'center',
    margin: '0 0 4px 0',
    lineHeight: 1.45,
    whiteSpace: 'pre-line',
  },
  leftFree: {
    color: '#f472b6',
    fontSize: '28px',
    fontWeight: 800,
    textAlign: 'center',
    margin: '0 0 20px 0',
    letterSpacing: '0.05em',
  },
  leftImageWrap: {
    width: '240px' /* ← reduced from 320 so it fits without overflow */,
    height: '240px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  leftImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  leftFeatures: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
  },
  leftFeatureRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  leftFeatureText: {
    color: '#ffffff',
    fontSize: '13px',
    lineHeight: 1.5,
  },

  /* ── Right panel ── */
  rightPanel: {
    flex: 1,
    minWidth: '340px',
    padding: '44px 36px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    backgroundColor: '#ffffff',
  },

  /* Close button */
  closeBtn: {
    position: 'absolute',
    top: '14px',
    right: '14px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
  },

  /* Title */
  title: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#0f1724',
    margin: '0 0 28px 0',
  },

  /* Form fields */
  fieldGroup: {
    marginBottom: '18px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '4px',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    color: '#111827',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    backgroundColor: '#ffffff',
    display: 'block',
  },

  /* Messages */
  errorText: {
    fontSize: '13px',
    color: '#e24b4a',
    margin: '0 0 12px 0',
  },
  successText: {
    fontSize: '13px',
    color: '#34C759',
    margin: '0 0 12px 0',
  },

  /* Primary button */
  btn: {
    width: '100%',
    padding: '13px',
    backgroundColor: '#1D4094' /* ← correct brand blue */,
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 700,
    letterSpacing: '0.05em',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'inherit',
    marginBottom: '18px',
  },

  /* Change phone link */
  changeBtn: {
    background: 'none',
    border: 'none',
    color: '#1D4094',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
    fontFamily: 'inherit',
    flexShrink: 0,
  },

  /* Resend */
  resendRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  timer: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
  },
  resendText: {
    fontSize: '13px',
    color: '#6b7280',
    textAlign: 'center',
  },
  resendLink: {
    color: '#1D4094',
    fontWeight: 600,
    cursor: 'pointer',
  },
  resendLinkDisabled: {
    color: '#9999A1',
    fontWeight: 400,
    cursor: 'default',
  },

  /* Switch login/signup */
  switchText: {
    fontSize: '13px',
    color: '#1D4094',
    textAlign: 'center',
    cursor: 'pointer',
    margin: 0,
    fontWeight: 500,
  },
};
